import React, { useEffect, useState } from 'react';
import api from '../services/api';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import { trackFormSubmit } from '../utils/ga4';
import './BazaarVendor.css';
import './Media.css';
import { getCmsImagePath } from '../cms/cmsData';
import { getArabicTranslation } from '../utils/arabicTranslations';

// Defaults shown if the dynamic fetch fails. Staff manage these via the admin
// dashboard (VIP tab) — see VIPFormOption on the backend.
const DEFAULT_VIP_TYPES = [
  { value: 'PRESS_INQUIRY',        label: 'Press Inquiry' },
  { value: 'MEDIA_CREDENTIALS',    label: 'VIP Credentials' },
  { value: 'INTERVIEW_REQUEST',    label: 'Interview Request' },
  { value: 'PHOTO_VIDEO_REQUEST',  label: 'Photography/Video Request' },
  { value: 'INFLUENCER_COVERAGE',  label: 'Influencer Coverage' },
  { value: 'GENERAL_QUESTION',     label: 'General VIP Question' },
];

const DEFAULT_COVERAGE_TOPICS = [
  { value: 'FESTIVAL_GROUNDS',  label: 'Festival grounds' },
  { value: 'ARTISTS',           label: 'Artists' },
  { value: 'VENDORS',           label: 'Vendors' },
  { value: 'FOOD_CULTURAL',     label: 'Food / Cultural programming' },
  { value: 'AMPHITHEATER',      label: 'Pacific Amphitheatre' },
  { value: 'OTHER',             label: 'Other' },
];

const CREDENTIAL_COUNTS = [
  { value: '',  label: 'Choose One' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
];

const Vip = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    website: '',
    vipType: '',
    coverageTopic: '',
    requestingCredentials: '',   // 'yes' | 'no' | ''
    credentialCount: '',
    hasOwnEquipment: '',         // 'yes' | 'no' | ''
    performingArtist: '',        // 'yes' | 'no' | ''
    performingArtistDay: '',
    mediaKitLink: '',
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [termsError, setTermsError] = useState(false);
  const [vipTypes, setVipTypes] = useState(DEFAULT_VIP_TYPES);
  const [coverageTopics, setCoverageTopics] = useState(DEFAULT_COVERAGE_TOPICS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getVipFormOptions();
        if (cancelled || !res?.success) return;
        const mt = res.data?.vip_type;
        const ct = res.data?.coverage_topic;
        if (Array.isArray(mt) && mt.length) setVipTypes(mt);
        if (Array.isArray(ct) && ct.length) setCoverageTopics(ct);
      } catch (_) {
        // Keep defaults on failure.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const nextValue = type === 'checkbox' ? checked : value;
      const next = {
        ...prev,
        [name]: nextValue,
      };
      if (name === 'performingArtist' && value !== 'yes') {
        next.performingArtistDay = '';
      }
      return next;
    });
    setError('');
    if (name === 'acceptTerms') setTermsError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = [];
    if (!formData.firstName) missing.push('First name');
    if (!formData.lastName) missing.push('Last name');
    if (!formData.email) missing.push('Email');
    if (!formData.phone) missing.push('Phone');
    if (!formData.organization) missing.push('VIP Outlet / Organization');
    if (!formData.website) missing.push('Organization Website Link (or Social)');
    if (!formData.vipType) missing.push('Choose VIP Type');
    if (!formData.requestingCredentials) missing.push('Are you requesting media credentials?');
    if (!formData.hasOwnEquipment) missing.push('Do you have your own equipment?');
    if (!formData.performingArtist) missing.push('Performing artist?');
    if (formData.performingArtist === 'yes' && !formData.performingArtistDay) missing.push('What day are you performing?');
    if (missing.length > 0) {
      setError(`Please fill in the following: ${missing.join(', ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!formData.acceptTerms) {
      setTermsError(true);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        website: formData.website,
        vip_type: formData.vipType,
        coverage_topic: formData.coverageTopic,
        requesting_credentials: formData.requestingCredentials === 'yes',
        credential_count:
          formData.requestingCredentials === 'yes' && formData.credentialCount
            ? parseInt(formData.credentialCount, 10)
            : null,
        has_own_equipment: formData.hasOwnEquipment === 'yes',
        performing_artist: formData.performingArtist === 'yes',
        performing_artist_day: formData.performingArtist === 'yes' ? formData.performingArtistDay : '',
        media_kit_link: formData.mediaKitLink,
        accept_terms: formData.acceptTerms,
      };

      const result = await api.submitVipRegistration(payload);

      if (result?.success) {
        setSuccess(true);
        setError('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        trackFormSubmit('vip_form_submit', {
          formName: 'vip_registration_form',
          formLocation: 'vip_page',
        });
        setTimeout(() => {
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            organization: '',
            website: '',
            vipType: '',
            coverageTopic: '',
            requestingCredentials: '',
            credentialCount: '',
            hasOwnEquipment: '',
            performingArtist: '',
            performingArtistDay: '',
            mediaKitLink: '',
            acceptTerms: false,
          });
          setSuccess(false);
        }, 5000);
      } else {
        setError(result?.error?.message || 'Failed to submit inquiry');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('VIP registration error:', err);
      if (err.message?.includes('Unexpected token') || err.message?.includes('not valid JSON')) {
        setError('Unable to connect to server. Please try again later or contact us directly at info@ocmenafestival.com');
      } else {
        setError(err.message || 'Failed to submit. Please try again.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const star = <span style={{ color: '#dc2626' }}>*</span>;

  return (
    <div className="page-wrapper">
      <AnnouncementBar />

      <section className="hero-section">
        <div className="hero-background-wrapper">
          <video src={'/hero-final.mp4'} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <h1 className="card-title">
            VIP Relations <span className="arabic-text on-white">{getArabicTranslation('VIP Relations')}</span>
          </h1>
          <p className="card-subtitle">
            Register VIP guests for OC MENA Festival and Pacific Amphitheatre access using the form below.
            Submit attendee information, VIP type, and credential details so our team can review and
            confirm the request.
          </p>

          {error && <div className="error-message" role="alert">{error}</div>}
          {success && <div className="success-message" role="status">Thank you! Your VIP registration has been submitted. We'll be in touch soon.</div>}

          <form onSubmit={handleSubmit} className="vendor-form media-form">
            <div className="form-group">
              <label htmlFor="firstName">First name {star}</label>
              <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last name {star}</label>
              <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email {star}</label>
              <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone {star}</label>
              <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="organization">VIP Outlet / Organization {star}</label>
              <input type="text" id="organization" name="organization" value={formData.organization} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="website">Organization Website Link (or Social) {star}</label>
              <input type="text" id="website" name="website" value={formData.website} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="vipType">Choose VIP Type {star}</label>
              <select id="vipType" name="vipType" value={formData.vipType} onChange={handleChange} required>
                <option value="" disabled>Choose One</option>
                {vipTypes.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="coverageTopic">What would you like to cover?</label>
              <select id="coverageTopic" name="coverageTopic" value={formData.coverageTopic} onChange={handleChange}>
                <option value="">Choose One</option>
                {coverageTopics.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Are you requesting media credentials? {star}</label>
              <div style={{ display: 'flex', gap: '24px', marginTop: '6px' }}>
                <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="radio" name="requestingCredentials" value="yes" checked={formData.requestingCredentials === 'yes'} onChange={handleChange} />
                  Yes
                </label>
                <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="radio" name="requestingCredentials" value="no" checked={formData.requestingCredentials === 'no'} onChange={handleChange} />
                  No
                </label>
              </div>
            </div>

            {formData.requestingCredentials === 'yes' && (
              <div className="form-group">
                <label htmlFor="credentialCount">How many credentials do you require?</label>
                <select id="credentialCount" name="credentialCount" value={formData.credentialCount} onChange={handleChange}>
                  {CREDENTIAL_COUNTS.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Do you have your own equipment? {star}</label>
              <div style={{ display: 'flex', gap: '24px', marginTop: '6px' }}>
                <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="radio" name="hasOwnEquipment" value="yes" checked={formData.hasOwnEquipment === 'yes'} onChange={handleChange} />
                  Yes
                </label>
                <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="radio" name="hasOwnEquipment" value="no" checked={formData.hasOwnEquipment === 'no'} onChange={handleChange} />
                  No
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Performing artist? {star}</label>
              <div style={{ display: 'flex', gap: '24px', marginTop: '6px' }}>
                <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="radio" name="performingArtist" value="yes" checked={formData.performingArtist === 'yes'} onChange={handleChange} />
                  Yes
                </label>
                <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input type="radio" name="performingArtist" value="no" checked={formData.performingArtist === 'no'} onChange={handleChange} />
                  No
                </label>
              </div>
            </div>

            {formData.performingArtist === 'yes' && (
              <div className="form-group">
                <label htmlFor="performingArtistDay">What day? {star}</label>
                <select id="performingArtistDay" name="performingArtistDay" value={formData.performingArtistDay} onChange={handleChange} required>
                  <option value="" disabled>Choose One</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                  <option value="sunday">Sunday</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="mediaKitLink">Do you have a link to a media kit?</label>
              <input type="text" id="mediaKitLink" name="mediaKitLink" value={formData.mediaKitLink} onChange={handleChange} />
            </div>

            <div
              className="form-group"
              style={termsError ? { padding: '12px', border: '2px solid #dc2626', borderRadius: '8px', backgroundColor: '#fef2f2' } : {}}
            >
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span>I understand that submitting this form does not guarantee media approval, credentials, or access.</span>
              </label>
              {termsError && (
                <p style={{ color: '#dc2626', fontSize: '13px', margin: '8px 0 0', fontWeight: '600' }}>
                  Please confirm the disclaimer above to continue
                </p>
              )}
            </div>

            <button type="submit" className="continue-btn" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath('lanterns', '/lanterns.webp')} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      <SponsorsSection />
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Vip;

